// ---------------------------------------------------------------------------
// One fetch of the dataset per URL, shared by every chart on the page, and one
// filtered view derived from it.
//
// "Kdo a co" mounts three charts over the same 1.1 MB file, so the request is
// cached by URL rather than made once per component. The cache is module-level
// and the page is a plain Django page, so it dies with the navigation — there
// is nothing to invalidate.
//
// `visible` is what charts draw: filtering happens once here, not once per
// component, and the derivations in cases.js never learn that a filter exists.
// They just get a shorter array.
// ---------------------------------------------------------------------------

import { computed, ref } from 'vue'

import { visibleContracts, visibleRows } from './cases.js'
import { filters } from './store.js'

const inFlight = new Map()

/** One request per URL, however many components ask for it. */
export function useJson(src, empty = []) {
    if (!inFlight.has(src)) {
        const data = ref(empty)
        const failed = ref(false)

        fetch(src)
            .then((response) => {
                if (!response.ok) throw new Error(response.status)
                return response.json()
            })
            .then((body) => {
                data.value = body
            })
            .catch(() => {
                failed.value = true
            })

        inFlight.set(src, { data, failed })
    }

    return inFlight.get(src)
}

export function useCases(src) {
    const { data: rows, failed } = useJson(src)

    return {
        rows,
        failed,
        visible: computed(() => visibleRows(rows.value, filters))
    }
}

/**
 * The other dataset: one row per contract rather than per decision.
 *
 * Everything that counts money or contracts reads this one. What counts
 * decisions — durations, the funnel, the register table — stays on useCases.
 */
export function useContracts(src) {
    const { data: rows, failed } = useJson(src)

    return {
        rows,
        failed,
        visible: computed(() => visibleContracts(rows.value, filters))
    }
}
